import { useCallback, useEffect, useState } from 'react';
import {
  MILLISECONDS_IN_SEC,
  SECONDS_IN_MIN,
} from '../utilities/constants/time';

type NotificationColor = 'blue' | 'green' | 'orange' | 'default';

interface UseAppointmentColorParams {
  startDate: string;
}

const THIRTY_MIN = 30 * SECONDS_IN_MIN * MILLISECONDS_IN_SEC;
const FIFTEEN_MIN = 15 * SECONDS_IN_MIN * MILLISECONDS_IN_SEC;
const FIVE_MIN = 5 * SECONDS_IN_MIN * MILLISECONDS_IN_SEC;

const COLORS_BASE = {
  blue: '#3991CE',
  green: '#38C576',
  orange: '#EE9E4E',
  default: '#EE9E4E',
};

const COLORS_TIME = {
  blue: '#2B43DA',
  green: '#12831B',
  orange: '#EE9E4E',
  default: null,
};

export function useAppointmentNotificationColor({
  startDate,
}: UseAppointmentColorParams): {
  timeColor: string | null;
  baseColor: string | null;
} {
  const [color, setColor] = useState<NotificationColor | null>(null);

  const checkColor = useCallback(() => {
    const now = Date.now();
    const appointmentStartTime = new Date(startDate).getTime();
    const timeDiff = appointmentStartTime - now;

    if (timeDiff <= FIVE_MIN && timeDiff > 0) {
      setColor('orange');
    } else if (timeDiff <= FIFTEEN_MIN && timeDiff > FIVE_MIN) {
      setColor('green');
    } else if (timeDiff <= THIRTY_MIN && timeDiff > FIFTEEN_MIN) {
      setColor('blue');
    } else {
      setColor('default');
    }
  }, [startDate]);

  useEffect(() => {
    checkColor();
    const intervalId = setInterval(checkColor, MILLISECONDS_IN_SEC);
    return () => clearInterval(intervalId);
  }, [checkColor]);

  return {
    baseColor: color && COLORS_BASE[color],
    timeColor: color && COLORS_TIME[color],
  };
}
